
import { defineComponent, reactive, ref, toRefs } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { PatientInfoType, AxiosErrorType } from '@/shared/types'
import { patientApi } from '@/shared/api'
import { ElMessage } from 'element-plus'
import { usePatientListing } from '@/shared/composables'
import { showErrorMessage } from '@/shared/utils'
import { useRouter } from 'vue-router'
import { INPUT_NAMES } from '@/widgets/WPatientCreationForm/constants/inputNames'

export default defineComponent({
  name: 'WPatientCreationForm',
  setup() {
    const router = useRouter()

    const { updatePatientListing } = usePatientListing()

    const patientCreationForm = ref<FormInstance>()

    const formInputRefs = reactive({
      surnameRef: undefined,
      nameRef: undefined,
      patronymicRef: undefined,
      dateRef: undefined,
      emailRef: undefined,
      cartNumberRef: undefined,
      phoneRef: undefined,
    })

    const patientCreationFormData: PatientInfoType = reactive({
      surname: '',
      name: '',
      patronymic: '',
      date: '',
      email: '',
      cartNumber: '',
      phone: '',
    })

    const clearForm = (): void => {
      patientCreationForm.value.resetFields()
    }

    const patientCreationValidationRules = reactive<FormRules>({
      surname: [
        { required: true, message: 'Введите фамилию', trigger: 'blur' },
      ],
      name: [{ required: true, message: 'Введите имя', trigger: 'blur' }],
      date: [
        { required: true, message: 'Введите дату рождения', trigger: 'blur' },
      ],
      email: [
        {
          type: 'email',
          message: 'Некорректный адрес эл.почты',
          trigger: 'blur',
        },
      ],
    })

    const createPatient = async (): Promise<number> => {
      const [error, response] = await patientApi.createPatient(
        patientCreationFormData
      )

      if (error) {
        const errors = Object.values(error).map(
          (message, index) => message[index]
        ) as string[]

        const axiosError: AxiosErrorType = {
          errors,
          status: error.status,
        }

        showErrorMessage(axiosError)

        return 0
      }

      ElMessage({
        message: 'Пациент создан',
        type: 'success',
      })

      updatePatientListing(true)

      clearForm()

      return response.data.patient_id
    }

    const handleChangeFocusedInput = (nextInput: string): void => {
      formInputRefs[nextInput].focus()
    }

    const handleCreatePatient = (form: FormInstance | undefined): void => {
      if (!form) return

      form.validate(async (valid): Promise<boolean> => {
        if (valid) {
          const patientId = await createPatient()

          if (patientId) {
            router.push({ name: 'PatientPage', params: { patientId } })
          }
        }

        return false
      })
    }

    return {
      INPUT_NAMES,
      ...toRefs(formInputRefs),
      patientCreationForm,
      patientCreationFormData,
      patientCreationValidationRules,
      handleChangeFocusedInput,
      handleCreatePatient,
    }
  },
})
